import { Stack, Typography } from '@mui/material';
import { CreateForm, DateInput, Dropdown, FormFieldsData, TextInput, useFormContext } from 'framework/form';
import { SideDrawer } from 'framework/ui/mui-builder';
import useSyberryTodayService from 'domain/people/syberryToday/syberryTodayService';
import { SyberryTodaySpentTimeActivity, type SyberryTodayWorkItem } from 'domain/people/syberryToday/types';
import { getSyberryTodaySpentTimeValidationSchema } from './syberryTodaySpentTimeValidationSchema';

const INITIAL_FORM_VALUES = {
  date: '',
  timeSpent: '',
  comment: '',
  activity: '',
};

const SPENT_TIME_ACTIVITY_OPTIONS = [
  { value: SyberryTodaySpentTimeActivity.OFFICE, label: 'Office' },
  { value: SyberryTodaySpentTimeActivity.HOME, label: 'Home' },
  { value: SyberryTodaySpentTimeActivity.TRANSIT, label: 'Transit' },
];

type SyberryTodaySpentTimeDrawerProps = {
  task: Nullable<SyberryTodayWorkItem>;
  onClose: () => void;
};

const getStringValue = (value: unknown) => (value === null || value === undefined ? '' : String(value));

export const buildSyberryTodaySpentTimePayload = (task: SyberryTodayWorkItem, values: FormFieldsData) => ({
  taskId: task.id,
  issueKey: task.issueKey,
  date: getStringValue(values.date),
  timeSpent: getStringValue(values.timeSpent),
  comment: getStringValue(values.comment),
  activity: values.activity as SyberryTodaySpentTimeActivity,
});

const SyberryTodaySpentTimeDrawerContent = ({ task }: { task: SyberryTodayWorkItem }) => {
  return (
    <Stack spacing={2.25}>
      <Stack direction="row" alignItems="baseline" flexWrap="wrap" sx={{ columnGap: 0.5, lineHeight: 1.35 }}>
        <Typography variant="h6">{task.title}</Typography>
        <Typography color="textSecondary">
          - {task.projectName} / {task.streamName}
        </Typography>
      </Stack>

      <DateInput source="date" label="Date" />
      <TextInput source="timeSpent" label="Time spent" placeholder="e.g. 1.5 or 1h 30m" isRequired />
      <TextInput source="comment" label="Comment" isRequired />
      <Dropdown
        source="activity"
        label="Activity"
        placeholder="Select activity"
        options={SPENT_TIME_ACTIVITY_OPTIONS}
        isRequired
      />
    </Stack>
  );
};

const SyberryTodaySpentTimeDrawerForm = ({ task, onClose }: SyberryTodaySpentTimeDrawerProps) => {
  const { submitForm = () => {}, isSubmitEnabled = true } = useFormContext();

  return (
    <SideDrawer
      isOpen={Boolean(task)}
      title="Spent Time"
      onClose={onClose}
      content={task ? <SyberryTodaySpentTimeDrawerContent task={task} /> : null}
      actions={{
        type: 'default',
        primaryAction: {
          label: 'Create',
          onClick: submitForm,
          disabled: !isSubmitEnabled,
        },
      }}
    />
  );
};

const SyberryTodaySpentTimeDrawer = ({ task, onClose }: SyberryTodaySpentTimeDrawerProps) => {
  const { createSpentTime } = useSyberryTodayService();
  const submitSpentTime = (values: FormFieldsData) =>
    task ? createSpentTime(buildSyberryTodaySpentTimePayload(task, values)) : Promise.resolve();

  return (
    <CreateForm
      key={task?.id ?? 'closed'}
      initialData={INITIAL_FORM_VALUES}
      submitFormFunc={submitSpentTime}
      getValidationSchemaFunc={getSyberryTodaySpentTimeValidationSchema}
      handleDirty={false}
      afterSubmit={{
        execute: onClose,
      }}
    >
      <SyberryTodaySpentTimeDrawerForm task={task} onClose={onClose} />
    </CreateForm>
  );
};

export default SyberryTodaySpentTimeDrawer;
